import { useAppSelector } from '../app/hooks';
import type { CSSProperties } from 'react';
import React from 'react';
import getThemeLookup from '../selectors/getThemeLookup';
import Text from './Text';
import TouchableOpacity from './TouchableOpacity';
import { TEXT_PROPERTIES } from '../utils/theme';
import combineStyles from '../utils/combineStyles';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';

export type SubSectionTitle = string | React.FC;

export interface SubSectionProps {
  action?: () => void;
  title?: SubSectionTitle;
  titleStyle?: CSSProperties;
  subtitle?: string;
  subtitleStyle?: CSSProperties;
  leftIcon?: () => JSX.Element;
  leftIconStyle?: CSSProperties;
  rightSlot?: () => JSX.Element | null;
  rightSlotAction?: () => void;
  active?: boolean;
}

const SubSection = ({
  action,
  title,
  titleStyle,
  subtitle,
  subtitleStyle,
  leftIcon,
  leftIconStyle,
  rightSlot,
  rightSlotAction,
  active,
}: SubSectionProps) => {
  const p = useAppSelector(getThemeLookup);
  const isMobile = useAppSelector(getDeviceTypeMobile);

  return (
    <div style={{ position: 'relative' }}>
      <TouchableOpacity onClick={action}>
        <div
          style={{
            ...styles.subSection,
            ...(isMobile && styles.subSectionMobile),
            ...(active && styles.subSectionActive),
            ...(active && p('subSectionActive', ['border', 'borderBottom'])),
          }}
        >
          <div style={styles.textWrapper}>
            <div style={styles.iconWrapper}>
              {!!leftIcon && (
                <span
                  style={combineStyles(
                    styles.icon,
                    leftIconStyle,
                    active && p('subSectionActive', ['color']),
                  )}
                >
                  {leftIcon()}
                </span>
              )}
              <div>
                {Boolean(title) && typeof title == 'string' ? (
                  <>
                    <Text
                      variant="body"
                      style={combineStyles(
                        p('modalSubSectionTitle', TEXT_PROPERTIES),
                        p('modalSubSection', ['color']),
                        active && p('subSectionActive', ['color']),
                        titleStyle,
                      )}
                    >
                      {title}
                    </Text>
                  </>
                ) : (
                  title
                )}
                {subtitle && (
                  <>
                    {title && <br />}

                    <Text
                      variant="body"
                      style={combineStyles(
                        p('modalSubSection', ['color']),
                        styles.subtitle,
                        subtitleStyle,
                      )}
                    >
                      {subtitle}
                    </Text>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </TouchableOpacity>
      {!!rightSlot && (
        <div
          style={combineStyles(styles.rightSlot, active && p('subSectionActive', ['color']))}
          onClick={() => {
            if (rightSlotAction) {
              rightSlotAction();
              return;
            }
            if (action) {
              action();
            }
          }}
        >
          {rightSlot()}
        </div>
      )}
    </div>
  );
};

export default SubSection;

const styles: Styles = {
  subSection: {
    display: 'flex',
    flexDirection: 'row',
    padding: '15px 20px',
    flex: 1,
    width: '100%',
    gap: 12,
    border: '2px solid transparent',
    borderBottom: '2px solid #e0e0e0',
    alignItems: 'stretch',
  },
  subSectionActive: {
    borderRadius: 8,
    borderBottom: undefined,
  },
  textWrapper: {
    flex: '0 1 80%',
  },
  subSectionMobile: {
    padding: '10px 20px',
  },
  subtitle: {
    lineHeight: '100%',
  },
  icon: {
    paddingRight: 20,
    fontSize: 24,
  },
  iconWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  rightSlot: {
    flex: '0 1 20%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: 0,
    alignSelf: 'stretch',
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    zIndex: 2,
    paddingRight: 20,
    cursor: 'pointer',
  },
};
